/*
  Beyours — shared "Look" quick-buy helpers.
  ========================================================================
  Shared by the Look PDP pattern and sections/looks-collection.liquid.
  Exposes window.BeyoursLook:

    fetchProduct(handle)      -> Promise<product JSON>   (session-cached)
    refetchProduct(handle)    -> Promise<product JSON>   (busts the cache)
    sizeLabel(product, v)     -> "M"   (short pill label)
    optionAxes(product)       -> { sizeIdx, secIdx, secName, sizes, secs }
    variantFor(p, size, sec)  -> variant | null
    addLook({items})          -> Promise<add.js response>
    openCartDrawer(response)  -> Promise<void>

  Children are NOT always single-option. The Classic Pant line is Size ×
  Length (Short/Regular/Tall), so a variant is only pinned down once both
  axes are picked — see optionAxes() below.

  Cart integration targets this theme specifically (Prestige/Focal family,
  Maestrooo). <cart-drawer id="mini-cart"> listens for "cart:refresh" on
  document.documentElement — NOT on document — and `open` is a boolean
  accessor, not a method. Dispatching on document, or calling drawer.open(),
  silently does nothing. See assets/bundle-page-v2.js for the same approach.
  ========================================================================
*/
(function () {
  if (window.BeyoursLook) return;

  /* ---------- Product JSON cache (per page session) ---------- */
  var cache = {};

  function fetchProduct(handle) {
    if (!handle) return Promise.reject(new Error('Missing product handle'));
    if (!cache[handle]) {
      cache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      }).then(function (r) {
        if (!r.ok) throw new Error('Could not load ' + handle);
        return r.json();
      });
      /* Never cache a rejection — a retry should be able to succeed. */
      cache[handle]['catch'](function () { delete cache[handle]; });
    }
    return cache[handle];
  }

  function refetchProduct(handle) {
    delete cache[handle];
    return fetchProduct(handle);
  }

  /* ---------- Size option handling ----------
     /products/x.js has returned `options` as both ["Size"] and
     [{name:"Size",...}] across Shopify versions — handle both. */
  function optionNames(product) {
    return (product && product.options || []).map(function (o) {
      return typeof o === 'string' ? o : ((o && o.name) || '');
    });
  }

  function sizeIndex(product) {
    var names = optionNames(product);
    for (var i = 0; i < names.length; i++) {
      if (String(names[i]).toLowerCase().indexOf('size') !== -1) return i;
    }
    return 0;
  }

  /* "XS . 35-36"" -> "XS". Mirrors snippets/look-component-card.liquid so the
     collection drawer and the Look PDP show identical pill labels. */
  function shortLabel(value) {
    if (value == null) return '';
    return String(value).split(' ')[0].split('.')[0].trim();
  }

  function sizeLabel(product, variant) {
    var idx = sizeIndex(product);
    var val = (variant.options && variant.options[idx]) || variant.title;
    return shortLabel(val) || String(variant.title || '');
  }

  /* Distinct values of one option, in the order the variants list them.
     Derived from the variants rather than product.options[i].values, which
     isn't present in every shape of the .js payload (see optionNames). */
  function optionValues(product, idx) {
    var seen = {}, out = [];
    (product && product.variants || []).forEach(function (v) {
      var val = v.options && v.options[idx];
      if (val == null || val === '' || seen[val]) return;
      seen[val] = 1;
      out.push(val);
    });
    return out;
  }

  /* The two axes a shopper picks on. `secIdx` is the first non-size option
     that actually varies (Length on the Classic Pants) — -1 when the product
     is single-axis, or when its second option has only one value, in which
     case that value is implied and never shown. */
  function optionAxes(product) {
    var names = optionNames(product);
    var sizeIdx = sizeIndex(product);
    var secIdx = -1;
    for (var i = 0; i < names.length; i++) {
      if (i === sizeIdx) continue;
      if (optionValues(product, i).length > 1) { secIdx = i; break; }
    }
    return {
      sizeIdx: sizeIdx,
      secIdx: secIdx,
      secName: secIdx === -1 ? '' : (names[secIdx] || 'Option'),
      sizes: optionValues(product, sizeIdx),
      secs: secIdx === -1 ? [] : optionValues(product, secIdx)
    };
  }

  /* Resolve a (size, secondary) pair to a variant. On a single-axis product
     `secValue` is ignored, so callers don't have to branch. */
  function variantFor(product, sizeValue, secValue) {
    var ax = optionAxes(product);
    var list = (product && product.variants) || [];
    for (var i = 0; i < list.length; i++) {
      var opts = list[i].options || [];
      if (opts[ax.sizeIdx] !== sizeValue) continue;
      if (ax.secIdx === -1 || opts[ax.secIdx] === secValue) return list[i];
    }
    return null;
  }

  /* ---------- Cart ---------- */
  function addLook(opts) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items: opts.items, sections: ['mini-cart'] })
    }).then(function (r) {
      return r.json().then(function (body) {
        if (!r.ok) {
          var err = new Error(body.description || body.message || 'Could not add the look.');
          err.body = body;
          throw err;
        }
        return body;
      });
    });
  }

  /* Refresh + open the theme's mini-cart using its own event contract. */
  function openCartDrawer(addResponse) {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (addResponse && addResponse.sections) cart.sections = addResponse.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: cart, openMiniCart: true }
        }));
      })
      ['catch'](function () { /* fall through to the direct open below */ })
      .then(function () {
        /* Last resort: open it directly. `open` is a setter, never a method. */
        setTimeout(function () {
          var drawer = document.getElementById('mini-cart');
          if (drawer && !drawer.hasAttribute('open')) {
            try { drawer.open = true; } catch (e) { drawer.setAttribute('open', ''); }
          }
        }, 600);
      });
  }

  window.BeyoursLook = {
    fetchProduct: fetchProduct,
    refetchProduct: refetchProduct,
    sizeLabel: sizeLabel,
    shortLabel: shortLabel,
    optionValues: optionValues,
    optionAxes: optionAxes,
    variantFor: variantFor,
    addLook: addLook,
    openCartDrawer: openCartDrawer
  };
})();
